import React, { useEffect, useState } from 'react';
import FormattedInput from '../FormatdInput/formatFunctions';
import { useUnits } from '../../context/unitsContext';
import './internalsFilterPanel.css';

export const EMPTY_INTERNALS_FILTERS = {
  status: '',
  name: '',
  cpf: '',
  mother: '',
  aka: '',
  unit_acronym: '',
  unit: '',
  pavilion: '',
  cell: '',
};

const EMPTY_DRAFT = {
  status: '',
  name: '',
  cpf: '',
  mother: '',
  aka: '',
  unitId: '',
  pavilionId: '',
  cellId: '',
};

const getUnitLabel = (unit) => (
  unit?.acronym || unit?.unit_acronym || unit?.name || `Unidade ${unit?.id}`
);

// Valor enviado ao backend: o filtro só aceita o acronym, nunca o nome completo da unidade.
const getUnitAcronym = (unit) => unit?.acronym || unit?.unit_acronym || '';

const getCellPavilionId = (cell) => Number(
  cell?.pavilion?.id ?? cell?.pavilion_id ?? cell?.pavilion
);

// Painel de filtros combinados de internos (Nome, CPF, Nome da mãe, Vulgo, Status,
// Unidade, Pavilhão e Cela). Mantém um rascunho local dos campos e só aciona a busca
// (onSearch) quando o usuário confirma, permitindo combinar todos os filtros numa única consulta.
// Pavilhão só é liberado com Unidade selecionada, e Cela só com Pavilhão selecionado.
function InternalsFilterPanel({ statusOptions = [], showUnitFilter = true, fixedUnitId = null, onSearch, onClear }) {
  const { listUnits, listPavillions, listCells } = useUnits();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [units, setUnits] = useState([]);
  const [pavilions, setPavilions] = useState([]);
  const [cells, setCells] = useState([]);

  const effectiveUnitId = fixedUnitId ?? (draft.unitId || null);
  const isPavilionEnabled = Boolean(effectiveUnitId);
  const isCellEnabled = Boolean(draft.pavilionId);

  // Lista de unidades para o select de Unidade (só quando ele é exibido).
  useEffect(() => {
    if (!showUnitFilter || fixedUnitId) return undefined;
    let isMounted = true;

    const fetchUnits = async () => {
      // summaryListUnit() não traz o campo acronym — só listUnits() traz, e é o
      // acronym que precisa aparecer (e ser enviado) neste filtro.
      const listResponse = await listUnits();
      const normalizedUnits = Array.isArray(listResponse)
        ? listResponse
        : (listResponse?.results || listResponse?.message || []);

      if (isMounted) setUnits(normalizedUnits);
    };

    fetchUnits();
    return () => { isMounted = false; };
  }, [showUnitFilter, fixedUnitId, listUnits]);

  // Pavilhões da unidade selecionada (ou da unidade fixa, na tela de internos por unidade).
  useEffect(() => {
    let isMounted = true;

    const fetchPavilions = async () => {
      if (!effectiveUnitId) {
        if (isMounted) setPavilions([]);
        return;
      }
      const response = await listPavillions({ unit: effectiveUnitId });
      const allPavilions = Array.isArray(response?.results) ? response.results : [];
      const filteredPavilions = allPavilions.filter(
        (pavilion) => String(pavilion?.unit) === String(effectiveUnitId)
      );
      if (isMounted) setPavilions(filteredPavilions);
    };

    fetchPavilions();
    return () => { isMounted = false; };
  }, [effectiveUnitId, listPavillions]);

  // Reseta pavilhão/cela selecionados sempre que a unidade efetiva muda.
  useEffect(() => {
    setDraft((prev) => ({ ...prev, pavilionId: '', cellId: '' }));
    setCells([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUnitId]);

  // Celas do pavilhão selecionado.
  useEffect(() => {
    let isMounted = true;

    const fetchCells = async () => {
      if (!draft.pavilionId) {
        if (isMounted) setCells([]);
        return;
      }
      const pavilionId = Number(draft.pavilionId);
      const response = await listCells({ pavilion: pavilionId });
      const allCells = Array.isArray(response?.results) ? response.results : [];
      const filteredCells = allCells.filter((cell) => getCellPavilionId(cell) === pavilionId);
      if (isMounted) setCells(filteredCells);
    };

    fetchCells();
    return () => { isMounted = false; };
  }, [draft.pavilionId, listCells]);

  const handleFieldChange = (field) => (event) => {
    const { value } = event.target;
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleUnitChange = (event) => {
    const { value } = event.target;
    setDraft((prev) => ({ ...prev, unitId: value, pavilionId: '', cellId: '' }));
  };

  const handlePavilionChange = (event) => {
    const { value } = event.target;
    setDraft((prev) => ({ ...prev, pavilionId: value, cellId: '' }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const selectedUnit = units.find((unit) => String(unit?.id) === String(draft.unitId));

    onSearch({
      status: draft.status,
      name: draft.name,
      cpf: draft.cpf,
      mother: draft.mother,
      aka: draft.aka,
      unit_acronym: selectedUnit ? getUnitAcronym(selectedUnit) : '',
      unit: selectedUnit?.id ?? '',
      // A cela já está ligada ao pavilhão no backend: se uma cela foi escolhida, o
      // pavilhão não precisa (e não deve) ser enviado junto.
      pavilion: draft.cellId ? '' : draft.pavilionId,
      cell: draft.cellId,
    });
  };

  const handleClear = () => {
    setDraft(EMPTY_DRAFT);
    setCells([]);
    onClear();
  };

  return (
    <form className="internals-filter-card" onSubmit={handleSubmit}>
      <div className="internals-filter-grid">
        <div className="internals-filter-field">
          <label htmlFor="filterStatus">Status</label>
          <select id="filterStatus" value={draft.status} onChange={handleFieldChange('status')}>
            {statusOptions.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="internals-filter-field">
          <label htmlFor="filterName">Nome</label>
          <FormattedInput
            type="nome"
            id="filterName"
            name="name"
            placeholder="Nome do interno"
            value={draft.name}
            onChange={handleFieldChange('name')}
          />
        </div>

        <div className="internals-filter-field">
          <label htmlFor="filterCpf">CPF</label>
          <FormattedInput
            type="cpf"
            id="filterCpf"
            name="cpf"
            placeholder="000.000.000-00"
            value={draft.cpf}
            onChange={handleFieldChange('cpf')}
          />
        </div>

        <div className="internals-filter-field">
          <label htmlFor="filterMother">Nome da mãe</label>
          <FormattedInput
            type="nome"
            id="filterMother"
            name="mother"
            placeholder="Nome da mãe"
            value={draft.mother}
            onChange={handleFieldChange('mother')}
          />
        </div>

        <div className="internals-filter-field">
          <label htmlFor="filterAka">Vulgo</label>
          <FormattedInput
            type="nome"
            id="filterAka"
            name="aka"
            placeholder="Vulgo"
            value={draft.aka}
            onChange={handleFieldChange('aka')}
          />
        </div>

        {showUnitFilter && !fixedUnitId && (
          <div className="internals-filter-field">
            <label htmlFor="filterUnit">Unidade</label>
            <select id="filterUnit" value={draft.unitId} onChange={handleUnitChange}>
              <option value="">Todas as unidades</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {getUnitLabel(unit)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="internals-filter-field">
          <label htmlFor="filterPavilion">Pavilhão</label>
          <select
            id="filterPavilion"
            value={draft.pavilionId}
            onChange={handlePavilionChange}
            disabled={!isPavilionEnabled}
          >
            <option value="">
              {isPavilionEnabled ? 'Selecione o pavilhão' : 'Selecione a unidade primeiro'}
            </option>
            {pavilions.map((pavilion) => (
              <option key={pavilion.id} value={pavilion.id}>
                {pavilion.name}
              </option>
            ))}
          </select>
        </div>

        <div className="internals-filter-field">
          <label htmlFor="filterCell">Cela</label>
          <select
            id="filterCell"
            value={draft.cellId}
            onChange={handleFieldChange('cellId')}
            disabled={!isCellEnabled}
          >
            <option value="">
              {isCellEnabled ? 'Selecione a cela' : 'Selecione o pavilhão primeiro'}
            </option>
            {cells.map((cell) => (
              <option key={cell.id} value={cell.id}>
                {cell.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="internals-filter-actions">
        <button type="button" className="btn-edit" onClick={handleClear}>
          Limpar filtros
        </button>
        <button type="submit" className="save-btn">
          Buscar
        </button>
      </div>
    </form>
  );
}

export default InternalsFilterPanel;
